import { supabase } from "@/integrations/supabase/client";

export interface UploadOptions {
  bucket: string;
  folder?: string;
  fileName?: string;
  maxSizeBytes?: number;
  allowedTypes?: string[];
  cacheControl?: string;
  skipCompression?: boolean;
}

export interface UploadResult {
  url: string;
  path: string;
  size: number;
}

export const storageService = {
  // Upload file to storage
  async uploadFile(file: File, options: UploadOptions): Promise<UploadResult> {
    const {
      bucket,
      folder = "",
      fileName,
      maxSizeBytes = 5 * 1024 * 1024,
      allowedTypes,
      cacheControl,
      skipCompression,
    } = options;

    // Validate file size
    if (file.size > maxSizeBytes) {
      throw new Error(
        `File size must be less than ${Math.round(
          maxSizeBytes / 1024 / 1024
        )}MB`
      );
    }

    // Validate file type
    if (allowedTypes && !allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not allowed`);
    }

    // Generate unique file name if not provided
    const fileExt = file.name.split(".").pop();
    const uniqueFileName =
      fileName ||
      `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = folder ? `${folder}/${uniqueFileName}` : uniqueFileName;

    // Upload file
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: cacheControl || "3600",
        upsert: false,
        skipCompression,
      });

    if (error) throw error;

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(bucket).getPublicUrl(data.path);

    return {
      url: publicUrl,
      path: data.path,
      size: file.size,
    };
  },

  // Upload avatar image
  async uploadAvatar(file: File, userId: string): Promise<string> {
    const isGif = file.type === 'image/gif';
    
    const result = await this.uploadFile(file, {
      bucket: "avatars",
      folder: userId,
      fileName: `avatar.${file.name.split(".").pop()}`,
      maxSizeBytes: isGif ? 5 * 1024 * 1024 : 2 * 1024 * 1024, // 5MB for GIFs, 2MB for others
      allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
      cacheControl: isGif ? 'no-cache' : '3600', // Don't cache GIFs
      skipCompression: isGif // Skip compression for GIFs
    });

    return result.url;
  },

  // Upload portfolio image
  async uploadPortfolioImage(file: File, userId: string): Promise<string> {
    const isGif = file.type === 'image/gif';
    
    const result = await this.uploadFile(file, {
      bucket: "portfolio-images",
      folder: userId,
      maxSizeBytes: 10 * 1024 * 1024, // 10MB
      allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
      cacheControl: isGif ? 'no-cache' : '3600', // Don't cache GIFs
      skipCompression: isGif // Skip compression for GIFs
    });

    return result.url;
  },

  // Upload case study image
  async uploadCaseStudyImage(file: File, userId: string): Promise<string> {
    const isGif = file.type === 'image/gif';
    const isVideo = file.type.startsWith('video/');
    
    const result = await this.uploadFile(file, {
      bucket: "case-study-images",
      folder: userId,
      maxSizeBytes: isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024, // 50MB for videos, 10MB for images
      allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/webm", "video/quicktime"],
      cacheControl: isGif ? 'no-cache' : '3600', // Don't cache GIFs
      skipCompression: isGif || isVideo // Skip compression for GIFs and videos
    });

    return result.url;
  },

  // Delete file from storage
  async deleteFile(bucket: string, path: string): Promise<void> {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) throw error;
  },

  // Get file URL
  getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);

    return data.publicUrl;
  },

  // Create signed URL for private files
  async createSignedUrl(
    bucket: string,
    path: string,
    expiresIn = 3600
  ): Promise<string> {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) throw error;
    return data.signedUrl;
  },

  // List files in a folder
  async listFiles(bucket: string, folder = ""): Promise<any[]> {
    const { data, error } = await supabase.storage.from(bucket).list(folder);

    if (error) throw error;
    return data || [];
  },

  // Get file metadata
  async getFileMetadata(bucket: string, path: string): Promise<any> {
    const { data, error } = await supabase.storage.from(bucket).download(path);

    if (error) throw error;
    return {
      size: data.size,
      type: data.type,
      lastModified: data.lastModified,
    };
  },
};
